/**
 * 对话工作流 · 火山方舟 Seedance 视频生成（经后端 /volc_seedance_video 代理）
 */

import { resolveApiUrl } from './apiBaseUrl'

function getAuthHeader() {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token') || ''
  return token || ''
}

function parseJsonSafe(text) {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

const SEEDANCE_RESOLUTIONS = ['480p', '720p', '1080p']

/** 与 ConversationWorkflow / 画布视频节点一致：提交 Seedance 前强制追加 */
const VIDEO_NO_SUBTITLES_MARKER = '【硬性要求】画面中不要出现任何字幕'
export const VIDEO_NO_SUBTITLES_SUFFIX =
  '\n\n【硬性要求】画面中不要出现任何字幕/对白文字/说明文字叠加（no subtitles, no captions, no on-screen text）。'

/**
 * 文生视频请求用提示词：强制不要字幕/画面文字叠加
 * @param {string} prompt
 * @returns {string}
 */
export function finalizeVideoPromptForApi(prompt) {
  let t = prompt == null ? '' : String(prompt).trim()
  if (!t) t = ' '
  if (t.includes(VIDEO_NO_SUBTITLES_MARKER)) return t
  return t + VIDEO_NO_SUBTITLES_SUFFIX
}

function normalizeSeedanceResolution(res) {
  const s = res != null ? String(res).trim().toLowerCase() : ''
  return SEEDANCE_RESOLUTIONS.includes(s) ? s : '720p'
}

/** 兼容 Dingo data 包装、方舟 task_id 等 */
function pickTaskIdFromCreateResponse(data) {
  if (!data || typeof data !== 'object') return ''
  const tryKeys = obj => {
    if (!obj || typeof obj !== 'object') return ''
    for (const k of ['id', 'task_id', 'taskId']) {
      const v = obj[k]
      if (v != null && String(v).trim()) return String(v).trim()
    }
    return ''
  }
  let id = tryKeys(data)
  if (id) return id
  id = tryKeys(data.data)
  if (id) return id
  id = tryKeys(data.result)
  if (id) return id
  if (data.raw && typeof data.raw === 'object') {
    id = tryKeys(data.raw)
    if (id) return id
    id = tryKeys(data.raw.data)
    if (id) return id
  }
  return ''
}

/** 查询接口返回体扁平化（兼容嵌套与 Dingo） */
function normalizeTaskQueryPayload(data) {
  if (!data || typeof data !== 'object') return data
  const inner = data.data && typeof data.data === 'object' ? { ...data, ...data.data } : data
  const videoUrl =
    inner.video_url ||
    (inner.content && inner.content.video_url) ||
    (inner.output && inner.output.video_url) ||
    ''
  const status = inner.status != null ? inner.status : data.status
  const id = pickTaskIdFromCreateResponse(inner) || pickTaskIdFromCreateResponse(data)
  return {
    ...inner,
    id: id || inner.id,
    status,
    video_url: videoUrl || inner.video_url
  }
}

/**
 * @param {Object} opts
 * @param {string} opts.prompt 文生视频提示词（可含工作流前缀）
 * @param {string} [opts.model] 推理接入点 / 模型 ID，默认由后端配置
 * @param {number} [opts.duration] 1～15 秒（写入后端 `--dur`）
 * @param {string} [opts.ratio] 如 16:9
 * @param {string} [opts.resolution] 480p | 720p | 1080p，写入后端 JSON `resolution`
 * @param {string[]|string} [opts.images] 可选参考图：url/dataURL/base64（数组或单张）
 * @param {AbortSignal} [opts.signal]
 */
export async function createSeedanceVideoTask(opts) {
  const { prompt, model, duration, ratio, resolution, images, signal } = opts || {}
  const ratioStr =
    ratio != null && String(ratio).trim() !== '' ? String(ratio).trim() : '16:9'
  const resolutionStr = normalizeSeedanceResolution(resolution)
  const imageList = Array.isArray(images)
    ? images.map(x => (x == null ? '' : String(x).trim())).filter(Boolean)
    : images
      ? [String(images).trim()].filter(Boolean)
      : []
  const res = await fetch(resolveApiUrl('/volc_seedance_video'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getAuthHeader()
    },
    body: JSON.stringify({
      prompt: finalizeVideoPromptForApi(prompt),
      ...(model ? { model } : {}),
      duration:
        duration != null && !Number.isNaN(Number(duration))
          ? Math.min(15, Math.max(1, Math.round(Number(duration))))
          : 5,
      /** 与界面选择一致，后端写入方舟文本参数 `--ratio` */
      ratio: ratioStr,
      resolution: resolutionStr,
      ...(imageList.length ? { images: imageList } : {})
    }),
    signal
  })
  const text = await res.text()
  const data = parseJsonSafe(text) || { raw: text }
  if (!res.ok) {
    const msg =
      (data && (data.message || data.error || data.errors)) ||
      text.slice(0, 400) ||
      `HTTP ${res.status}`
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
  const taskId = pickTaskIdFromCreateResponse(data)
  if (!taskId) {
    throw new Error(
      '创建任务成功但未解析到任务 id，请检查网关/Dingo 是否包装了 data。响应片段：' + text.slice(0, 400)
    )
  }
  return { ...data, id: taskId }
}

/**
 * @param {string} taskId
 * @param {AbortSignal} [signal]
 */
export async function getSeedanceVideoTask(taskId, signal) {
  const id = encodeURIComponent(String(taskId || '').trim())
  if (!id) throw new Error('缺少 task_id')
  const res = await fetch(resolveApiUrl(`/volc_seedance_video/${id}`), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getAuthHeader()
    },
    signal
  })
  const text = await res.text()
  const data = parseJsonSafe(text) || {}
  if (!res.ok) {
    const msg = data.message || data.error || text.slice(0, 400) || `HTTP ${res.status}`
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg))
  }
  return normalizeTaskQueryPayload(data)
}

/** 从任务查询结果中取出可播放的视频地址（兼容嵌套字段） */
export function pickSeedanceTaskVideoUrl(info) {
  if (!info || typeof info !== 'object') return ''
  const c = info.content && typeof info.content === 'object' ? info.content : null
  const o = info.output && typeof info.output === 'object' ? info.output : null
  const u =
    info.video_url ||
    info.videoUrl ||
    (c && (c.video_url || c.videoUrl || c.url)) ||
    (o && (o.video_url || o.videoUrl || o.url)) ||
    ''
  return String(u || '').trim()
}
